import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = ["Best gift under ₹500?", "I have oily, acne-prone skin", "Beard care routine for beginners"];

export const AiChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("velvet_ai_session") || "");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + data.delta };
              return copy;
            });
          }
          if (data.session_id) {
            setSessionId(data.session_id);
            localStorage.setItem("velvet_ai_session", data.session_id);
          }
          if (data.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: "I'm resting for a moment — please try again shortly." };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connection hiccup — try again?" };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-chat-toggle"
        aria-label="Ask ViRa AI"
        className="fixed bottom-6 right-6 z-[70] w-14 h-14 rounded-full bg-gold text-white shadow-lg shadow-pink-300/50 flex items-center justify-center hover:bg-champagne hover:scale-105 transition-all duration-300"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-3 bottom-20 sm:inset-x-auto sm:right-6 sm:bottom-24 z-[70] sm:w-[380px] h-[min(540px,calc(100dvh-8.5rem))] bg-white border border-zinc-200 shadow-2xl shadow-pink-200/40 flex flex-col overflow-hidden rounded-xl"
            data-testid="ai-chat-panel"
            data-lenis-prevent
          >
            <div className="px-5 py-4 border-b border-zinc-100 bg-gradient-to-r from-pink-50 to-white">
              <p className="font-serif text-lg text-zinc-900 flex items-center gap-2">
                <Sparkles size={16} className="text-gold" /> Ask ViRa
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mt-0.5">Your AI beauty advisor</p>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-3 pt-2">
                  <p className="text-sm text-zinc-500 font-serif italic">Tell me your skin, hair or grooming goal — I'll pick from our catalog.</p>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`ai-suggestion-${s.slice(0, 12).replace(/\W/g, "-")}`}
                      className="block w-full text-left text-xs text-zinc-600 border border-zinc-200 px-4 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors duration-200"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed rounded-2xl ${m.role === "user" ? "bg-gold text-white rounded-br-sm" : "bg-zinc-100 text-zinc-700 rounded-bl-sm"}`}>
                    {m.text || (streaming && i === messages.length - 1 ? <span className="animate-pulse">Thinking…</span> : "")}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="border-t border-zinc-100 p-3 flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about products…"
                data-testid="ai-chat-input"
                className="flex-1 bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 rounded-full focus:outline-none focus:border-gold/60 transition-colors duration-300"
              />
              <button
                onClick={() => send()}
                disabled={streaming}
                data-testid="ai-chat-send"
                aria-label="Send"
                className="w-11 h-11 rounded-full bg-gold text-white flex items-center justify-center hover:bg-champagne transition-colors duration-300 disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
